import {
  Info,
  ListChecks,
  Workflow,
  Clock,
  UserCheck,
  ClipboardCheck,
} from "lucide-react";
import {
  legalTheme,
  ScrollProgressRail,
  LegalHero,
  SectionNav,
  MobileSectionPills,
  LegalSection,
  ContactSection,
  useActiveSection,
} from "../components/legal/LegalKit";

const sections = [
  {
    id: "overview",
    no: 1,
    title: "Overview",
    icon: Info,
    paragraphs: [
      "At UniBcomp, we value transparency and the trust our clients, partners, and website visitors place in us. This Grievance Redressal Policy outlines how you can raise concerns related to our services, projects, payments, or website, and how we handle them responsibly.",
      "We aim to review every concern carefully and resolve issues at the earliest possible stage.",
    ],
  },
  {
    id: "what-can-be-reported",
    no: 2,
    title: "What Issues Can Be Reported",
    icon: ListChecks,
    intro: "You may raise a grievance for concerns such as:",
    items: [
      "Service or project delivery related concerns",
      "Payment, billing, or invoicing issues",
      "Account or client portal access problems",
      "Recruitment or internship related concerns",
      "Security or privacy concerns",
      "Any other business or website related issue",
    ],
  },
  {
    id: "resolution-process",
    no: 3,
    title: "Grievance Resolution Process",
    icon: Workflow,
    paragraphs: [
      "Once a grievance is submitted, our support team reviews the details and may contact you for additional information if required. We work closely with the relevant department or team to investigate and resolve the issue appropriately.",
    ],
  },
  {
    id: "response-timeline",
    no: 4,
    title: "Response Timeline",
    icon: Clock,
    items: [
      "Grievances are acknowledged within 48 hours.",
      "Resolution is typically provided within 7 business days, depending on the complexity of the issue.",
    ],
  },
  {
    id: "grievance-officer",
    no: 5,
    title: "Grievance Officer",
    icon: UserCheck,
    intro:
      "If your concern is not resolved satisfactorily, you may escalate it to our designated Grievance Officer.",
    items: [
      "Name: Rohit Sharma",
      "Designation: Grievance Officer",
      "Company: UniBcomp Networks Private Limited",
      "Email: grievance@unibcomp.net",
      "Working Hours: Monday to Saturday, 10:00 AM – 6:00 PM",
    ],
  },
  {
    id: "user-responsibility",
    no: 6,
    title: "User Responsibility",
    icon: ClipboardCheck,
    paragraphs: [
      "Please provide accurate information and relevant details when submitting a grievance to help us resolve the matter efficiently.",
      "UniBcomp may update this Grievance Redressal Policy from time to time. Continued use of the website constitutes acceptance of the updated policy.",
    ],
  },
];

const sectionIds = [...sections.map((s) => s.id), "contact-us"];

export default function GrievanceRedressal() {
  const activeId = useActiveSection(sectionIds);

  return (
    <div className="w-full" style={{ backgroundColor: legalTheme.surface }}>
      <ScrollProgressRail />

      <LegalHero
        eyebrow="Legal"
        title={["Grievance", "Redressal Policy"]}
        HeroIcon={UserCheck}
        meta={
          <>
            <span className="inline-flex items-center gap-2">
              <UserCheck size={15} style={{ color: legalTheme.gold }} />
              Last Updated: July 2026
            </span>
            <span className="hidden sm:inline">·</span>
            <span>UniBcomp Networks Private Limited</span>
          </>
        }
        description="We are committed to addressing your concerns fairly, transparently, and promptly to ensure a positive experience with UniBcomp Networks Private Limited."
        agreementNote="For any complaints or concerns, please contact us at support@unibcomp.net."
      />

      <MobileSectionPills sections={sections} activeId={activeId} />

      <section className="relative py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[240px_1fr] gap-16">
          <SectionNav sections={sections} activeId={activeId} />

          <div className="min-w-0 max-w-3xl">
            {sections.map((section) => (
              <LegalSection key={section.id} section={section} />
            ))}

            <ContactSection
              no={7}
              intro="For any questions regarding this policy, please contact us:"
              cards={[
                {
                  label: "General Enquiries",
                  desc: "For any complaints or concerns about our services or website.",
                  email: "support@unibcomp.net",
                },
                {
                  label: "Grievance Officer",
                  desc: "To escalate an unresolved concern directly to our Grievance Officer.",
                  email: "grievance@unibcomp.net",
                },
              ]}
            />

            <div className="mt-8 pt-8 border-t text-sm" style={{ borderColor: legalTheme.border, color: legalTheme.inkMuted }}>
              © 2026 UniBcomp Networks Private Limited. All rights reserved.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}